import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Calendar as CalendarIcon, Clock } from "lucide-react";

/**
 * DateTimePicker
 * - Value: ISO-ish string. Stored as "YYYY-MM-DD" (date only) or "YYYY-MM-DDTHH:mm" (with time).
 * - Display: DD/MM/YYYY, hh:mm AM/PM  (or DD/MM/YYYY when no time).
 * - Time is OPTIONAL (as requested).
 * - Past dates are disabled.
 */
export default function DateTimePicker({ label, value, onChange, testid, allowClear = true }) {
  const [open, setOpen] = useState(false);

  const parsed = useMemo(() => parseValue(value), [value]);
  const [date, setDate] = useState(parsed.date);            // Date | undefined
  const [hour12, setHour12] = useState(parsed.hour12 ?? ""); // "" | "01".."12"
  const [minute, setMinute] = useState(parsed.minute ?? ""); // "" | "00".."59"
  const [meridiem, setMeridiem] = useState(parsed.meridiem ?? "AM"); // "AM" | "PM"

  // Sync internal state when value prop changes externally
  React.useEffect(() => {
    const p = parseValue(value);
    setDate(p.date);
    setHour12(p.hour12 ?? "");
    setMinute(p.minute ?? "");
    setMeridiem(p.meridiem ?? "AM");
  }, [value]);

  const commit = (d, h, m, mer) => {
    if (!d) { onChange(""); return; }
    const iso = fmtISO(d);
    if (h && m) {
      const h24 = to24h(h, mer);
      onChange(`${iso}T${h24}:${m}`);
    } else {
      onChange(iso);
    }
  };

  const onDatePick = (d) => {
    setDate(d);
    commit(d, hour12, minute, meridiem);
  };
  const onTimeChange = (h, m, mer) => {
    setHour12(h); setMinute(m); setMeridiem(mer);
    commit(date, h, m, mer);
  };
  const clearAll = () => {
    setDate(undefined); setHour12(""); setMinute(""); setMeridiem("AM");
    onChange("");
    setOpen(false);
  };

  const displayLabel = useMemo(() => formatDisplay(value), [value]);

  const today = new Date(); today.setHours(0, 0, 0, 0);

  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className="w-full border border-border rounded px-3 py-2 text-sm text-left flex items-center gap-2 bg-white hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          >
            <CalendarIcon size={14} className="text-slate-400 shrink-0" />
            <span className={displayLabel ? "text-slate-900" : "text-slate-400"}>
              {displayLabel || "DD/MM/YYYY, hh:mm AM/PM"}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0 overflow-hidden" data-testid={`${testid}-popover`}>
          <div className="p-2">
            <Calendar
              mode="single"
              selected={date}
              onSelect={onDatePick}
              disabled={{ before: today }}
              initialFocus
            />
          </div>
          <div className="border-t border-slate-100 p-3 bg-slate-50/50">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500 mb-2">
              <Clock size={12} /> Time <span className="text-slate-400 normal-case tracking-normal">(optional)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <select
                data-testid={`${testid}-hh`}
                value={hour12}
                onChange={(e) => onTimeChange(e.target.value, minute || (e.target.value ? "00" : ""), meridiem)}
                className="border border-slate-200 rounded px-2 py-1 text-sm bg-white"
              >
                <option value="">HH</option>
                {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((h) => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
              <span className="text-slate-400">:</span>
              <select
                data-testid={`${testid}-mm`}
                value={minute}
                onChange={(e) => onTimeChange(hour12 || (e.target.value ? "12" : ""), e.target.value, meridiem)}
                className="border border-slate-200 rounded px-2 py-1 text-sm bg-white"
              >
                <option value="">MM</option>
                {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <div className="ml-1 inline-flex border border-slate-200 rounded overflow-hidden">
                {["AM", "PM"].map((m) => (
                  <button
                    type="button"
                    key={m}
                    data-testid={`${testid}-${m.toLowerCase()}`}
                    onClick={() => onTimeChange(hour12, minute, m)}
                    className={`px-2 py-1 text-xs font-medium ${meridiem === m ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => onTimeChange("", "", meridiem)}
                data-testid={`${testid}-clear-time`}
                className="ml-auto text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
              >
                Clear time
              </button>
            </div>
            {allowClear && (
              <div className="pt-2 mt-2 border-t border-slate-100 flex justify-between">
                <button type="button" onClick={clearAll} data-testid={`${testid}-clear`} className="text-xs text-red-500 hover:text-red-700">
                  Clear
                </button>
                <button type="button" onClick={() => setOpen(false)} data-testid={`${testid}-done`} className="text-xs px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700">
                  Done
                </button>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ---------- helpers ----------

function parseValue(v) {
  if (!v || typeof v !== "string") return { date: undefined };
  // Accept "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm"
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (!m) return { date: undefined };
  const [_, y, mo, d, hh, mm] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  if (hh == null || mm == null) return { date };
  const h24 = Number(hh);
  const meridiem = h24 >= 12 ? "PM" : "AM";
  const h12 = ((h24 % 12) || 12);
  return { date, hour12: String(h12).padStart(2, "0"), minute: mm, meridiem };
}

function fmtISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function to24h(h12, meridiem) {
  let h = Number(h12);
  if (meridiem === "AM") { if (h === 12) h = 0; }
  else { if (h !== 12) h += 12; }
  return String(h).padStart(2, "0");
}

function formatDisplay(v) {
  const p = parseValue(v);
  if (!p.date) return "";
  const d = p.date;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  const base = `${dd}/${mm}/${yy}`;
  if (p.hour12 && p.minute) return `${base}, ${p.hour12}:${p.minute} ${p.meridiem}`;
  return base;
}
